import { Component, DestroyRef, effect, inject, input, output } from '@angular/core';
import { FilterItem, SelectOption } from './filter-component.model';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { LocalizePipe } from "../../pipes/LocalizePipe";
import { CommonModule } from '@angular/common';
import { CustomMultiselectComponent } from "./custom-multiselect-component/custom-multiselect-component";
import { GovSelectComponent } from "../gov-select-component/gov-select-component";

@Component({
  selector: 'app-filter-component',
  imports: [CommonModule, LocalizePipe, ReactiveFormsModule, CustomMultiselectComponent, GovSelectComponent],
  templateUrl: './filter-component.html',
  styleUrl: './filter-component.css',
})
export class FilterComponent {
  private fb = inject(FormBuilder);
  private httpClient = inject(HttpClient);
  private destroyRef = inject(DestroyRef);

  config = input<FilterItem[]>([]);
  // hodnoty ze sessionstorage
  initialValues = input<any>({});
  filterSubmit = output<any>();
  optionsMap: { [key: string]: SelectOption[]} = {};
  form: FormGroup | undefined;
  private dependencySubscriptions?: Subscription;
  private optionRequestSeq: { [key: string]: number } = {};

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.dependencySubscriptions?.unsubscribe();
    });

    effect(() => {
      const currentConfig = this.config();
      const initVals = this.initialValues();

      if (currentConfig && currentConfig.length > 0) {
        this.dependencySubscriptions?.unsubscribe();
        this.buildForm(currentConfig);

        if (this.form && initVals && Object.keys(initVals).length > 0) {
          this.form.patchValue(this.coerceInitialValues(currentConfig, initVals), { emitEvent: false });
        }

        this.watchDependentFilters(currentConfig);
        this.loadSelectOptions(currentConfig);
      }
    });
  }

  private buildForm(items: FilterItem[]) {
    const group: any = {};

    items.forEach(item => {
      const defaultValue = item.type === 'multiselect' ? [] : '';
      group[item.column] = [defaultValue];
    });

    this.form = this.fb.group(group);
  }

  private loadSelectOptions(items: FilterItem[]) {
    items.forEach(item => {
      if ((item.type === 'select' || item.type === 'multiselect') && item.source) {
        const seq = (this.optionRequestSeq[item.column] ?? 0) + 1;
        this.optionRequestSeq[item.column] = seq;

        const url = this.applyDependencyParams(item, item.source);
        if (url === null) {
          this.optionsMap[item.column] = [];
          const stale = this.form?.get(item.column);
          if (stale && this.hasValue(stale.value)) {
            stale.setValue(item.type === 'multiselect' ? [] : '');
          }
          return;
        }

        this.httpClient.get<SelectOption[]>(url).subscribe({
          next: data => {
            if (this.optionRequestSeq[item.column] !== seq) return;
            if (item.type === 'multiselect') {
              this.optionsMap[item.column] = data.filter(opt => opt.value != '');
            }
            else {
              this.optionsMap[item.column] = data;
            }
          },
          error: () => {
            if (this.optionRequestSeq[item.column] !== seq) return;
            this.optionsMap[item.column] = [];
          },
        });
      }
    });
  }

  private watchDependentFilters(items: FilterItem[]) {
    this.dependencySubscriptions = new Subscription();

    items
      .filter(item => !!item.depends_on?.length && !!item.depends_on_param?.length)
      .forEach(item => {
        const parents = item.depends_on ?? [];
        parents.forEach(parentColumn => {
          const parentControl = this.form?.get(parentColumn);
          if (!parentControl) return;
          this.dependencySubscriptions!.add(parentControl.valueChanges.subscribe(() => {
            const childControl = this.form?.get(item.column);
            if (childControl && this.hasValue(childControl.value)) {
              childControl.setValue(item.type === 'multiselect' ? [] : '');
            }
            this.loadSelectOptions([item]);
          }));
        });
      });
  }

  private applyDependencyParams(item: FilterItem, source: string): string | null {
    const cols = item.depends_on ?? [];
    const params = item.depends_on_param ?? [];
    if (cols.length === 0 && params.length === 0) return source;
    if (cols.length !== params.length || cols.length === 0) {
      console.warn('filter-component: invalid depends_on/depends_on_param metadata', item.column, cols, params);
      return null;
    }

    const url = new URL(source, window.location.origin);
    for (let i = 0; i < cols.length; i++) {
      const value = this.form?.get(cols[i])?.value;
      // návrat source namísto null, aby se závislý select naplnil všema hodnotama namísto ničeho
      if (!this.hasValue(value)) return source;
      const encoded = Array.isArray(value) ? value.join(',') : String(value);
      url.searchParams.set(params[i], encoded);
    }
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : url.toString();
  }

  private coerceInitialValues(items: FilterItem[], values: Record<string, any>): Record<string, any> {
    const out: Record<string, any> = { ...values };
    items.forEach(item => {
      if (item.type === 'multiselect' && typeof out[item.column] === 'string') {
        const s = (out[item.column] as string).trim();
        out[item.column] = s === '' ? [] : s.split(',').map(p => p.trim()).filter(p => p !== '');
      }
    });
    return out;
  }

  private hasValue(v: any): boolean {
    if (v === null || v === undefined) return false;
    if (Array.isArray(v)) return v.length > 0;
    return String(v).trim() !== '';
  }

  onSubmit() {
    const rawValues = this.form?.value;
    const cleanValues: any = {};

    Object.keys(rawValues).forEach(key => {
      const val = rawValues[key];
      // Pokud to není null, undefined nebo prázdný string/pole
      if (val !== null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
        cleanValues[key] = val;
      }
    });

    this.filterSubmit.emit(cleanValues);
  }

  onReset() {
    this.form?.reset();
    
    // Pro multiselecty musíme nastavit [] místo null
    this.config().forEach(c => {
        if(c.type === 'multiselect') {
            this.form?.get(c.column)?.setValue([]);
        }
    });

    this.filterSubmit.emit({});
  }

  // Metoda vrátí true, pokud prvek má nějakou hodnotu
  isControlFilled(key: string): boolean {
    const control = this.form?.get(key);
    const value = control?.value;

    if (value === null || value === undefined) {
      return false;
    }

    //kontrola prázdného pole
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    // kontrola prázdného stringu
    // (!!value převede string na boolean: '' -> false, 'abc' -> true)
    return !!value.toString().trim();
  }
}
